import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import StatusBadge from '../../../components/ui/StatusBadge';
import { useAuth } from '../../../auth/AuthProvider';
import { supabase } from '../../../lib/supabaseClient';
import { getCachedSignedUrl } from '../../../utils/storage';

const ContentModerationPanel = () => {
  const { user } = useAuth();
  const [filterType, setFilterType] = useState('all');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [flaggedContent, setFlaggedContent] = useState([]);


  const filterOptions = [
    { value: 'all', label: '모든 유형' },
    { value: 'proof', label: '증명' },
    { value: 'challenge', label: '챌린지' }
  ];

  const filteredContent = useMemo(
    () =>
      filterType === 'all'
        ? flaggedContent
        : flaggedContent?.filter((item) => item?.type === filterType),
    [filterType, flaggedContent]
  );

  const loadReports = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    setLoadError('');
    try {
      const { data: reportRows, error } = await supabase
        .from('ch_reports')
        .select('id, created_at, reporter_id, target_type, proof_id, challenge_id, reason, details, status')
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) {
        setFlaggedContent([]);
        setLoadError(error?.message || '신고 목록을 불러오지 못했습니다.');
        return;
      }

      const rows = reportRows || [];

      const reporterIds = Array.from(new Set(rows.map((r) => r.reporter_id).filter(Boolean)));
      const proofIds = Array.from(new Set(rows.filter((r) => r.target_type === 'proof').map((r) => r.proof_id).filter(Boolean)));
      const challengeIds = Array.from(new Set(rows.filter((r) => r.target_type === 'challenge').map((r) => r.challenge_id).filter(Boolean)));

      const [{ data: reporters }, { data: proofs }, { data: challenges }] = await Promise.all([
        reporterIds.length > 0
          ? supabase
            .from('ch_profiles')
            .select('id, username, avatar_url')
            .in('id', reporterIds)
          : Promise.resolve({ data: [] }),
        proofIds.length > 0
          ? supabase
            .from('ch_proofs')
            .select('id, title, description, image_path, submitted_at')
            .in('id', proofIds)
          : Promise.resolve({ data: [] }),
        challengeIds.length > 0
          ? supabase
            .from('ch_challenges')
            .select('id, title, description, created_at')
            .in('id', challengeIds)
          : Promise.resolve({ data: [] }),
      ]);

      const reporterById = new Map((reporters || []).map((p) => [p.id, p]));
      const proofById = new Map((proofs || []).map((p) => [p.id, p]));
      const challengeById = new Map((challenges || []).map((c) => [c.id, c]));

      const grouped = new Map();
      rows.forEach((r) => {
        const key = r.target_type === 'proof' ? `proof:${r.proof_id}` : `challenge:${r.challenge_id}`;
        if (!key.includes('null')) {
          const cur = grouped.get(key) || [];
          cur.push(r);
          grouped.set(key, cur);
        }
      });

      const items = await Promise.all(
        Array.from(grouped.entries()).map(async ([key, reports]) => {
          const latest = reports[0];
          const isProof = latest.target_type === 'proof';
          const targetId = isProof ? latest.proof_id : latest.challenge_id;

          const reporter = reporterById.get(latest.reporter_id);
          const content = isProof ? proofById.get(targetId) : challengeById.get(targetId);

          let imageUrl = '';
          if (isProof && content?.image_path) {
            imageUrl = await getCachedSignedUrl({ bucket: 'proof-images', path: content.image_path });
          }

          const derivedStatus = reports.some((x) => x.status === 'pending')
            ? 'pending'
            : reports.some((x) => x.status === 'rejected')
              ? 'rejected'
              : 'approved';

          return {
            id: key,
            type: latest.target_type,
            targetId,

            image: imageUrl,
            imageAlt: content?.title ? `Reported content: ${content.title}` : 'Reported content',
            title: content?.title || '(제목 없음)',
            description: content?.description || '',

            userAvatar: reporter?.avatar_url || '',
            userAvatarAlt: reporter?.username ? `${reporter.username} avatar` : 'reporter avatar',
            userName: reporter?.username || '알 수 없음',

            reason: latest.reason,
            reportDate: latest.created_at ? String(latest.created_at).slice(0, 10) : '',
            reportCount: reports.length,
            status: derivedStatus,

            reportIds: reports.map((x) => x.id),
            latestReportDetails: latest.details || '',
          };
        })
      );

      setFlaggedContent(items);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      setSelectedItems(filteredContent?.map((item) => item?.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) =>
    prev?.includes(itemId) ? prev?.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const handleViewDetail = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  const updateStatusForItem = async (item, nextStatus) => {
    if (!item?.reportIds?.length) return;
    if (!user?.id) return;

    const { error } = await supabase
      .from('ch_reports')
      .update({
        status: nextStatus,
        reviewed_at: new Date().toISOString(),
        reviewed_by: user.id,
      })
      .in('id', item.reportIds);

    if (error) {
      alert(error?.message || '처리에 실패했습니다.');
      return;
    }

    await loadReports();
  };

  const updateStatusForSelected = async (nextStatus) => {
    if (!selectedItems?.length) return;
    if (!user?.id) return;

    const targets = selectedItems
      .map((id) => filteredContent?.find((x) => x.id === id))
      .filter(Boolean);
    const reportIds = targets.flatMap((t) => t.reportIds || []).filter(Boolean);
    if (reportIds.length === 0) return;

    const { error } = await supabase
      .from('ch_reports')
      .update({
        status: nextStatus,
        reviewed_at: new Date().toISOString(),
        reviewed_by: user.id,
      })
      .in('id', reportIds);

    if (error) {
      alert(error?.message || '일괄 처리에 실패했습니다.');
      return;
    }

    setSelectedItems([]);
    await loadReports();
  };

  const handleApprove = (itemId) => {
    const item = filteredContent?.find((x) => x.id === itemId);
    updateStatusForItem(item, 'approved');
  };

  const handleReject = (itemId) => {
    const item = filteredContent?.find((x) => x.id === itemId);
    updateStatusForItem(item, 'rejected');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: '대기 중', tone: 'pending' },
      reviewed: { label: '검토됨', tone: 'reviewed' },
      approved: { label: '승인됨', tone: 'approved' },
      rejected: { label: '거부됨', tone: 'rejected' },
    };
    const config = statusConfig?.[status] || statusConfig.pending;
    return <StatusBadge label={config.label} tone={config.tone} />;
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-warning/20 rounded-md flex items-center justify-center">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">신고된 콘텐츠</h3>
            <p className="text-sm text-muted-foreground caption">{filteredContent?.length}개 항목</p>
          </div>
        </div>
        <Select
          options={filterOptions}
          value={filterType}
          onChange={setFilterType}
          placeholder="유형 필터" />

      </div>
      {isLoading ? (
        <div className="bg-card rounded-lg shadow-gaming-md p-6">
          <p className="text-sm text-muted-foreground caption">로딩 중...</p>
        </div>
      ) : loadError ? (
        <div className="bg-card rounded-lg shadow-gaming-md p-6">
          <p className="text-sm text-destructive">{loadError}</p>
        </div>
      ) : filteredContent?.length === 0 ? (
        <div className="bg-card rounded-lg shadow-gaming-md p-6">
          <p className="text-sm text-muted-foreground caption">표시할 신고 항목이 없습니다.</p>
        </div>
      ) : null}
      {selectedItems?.length > 0 &&
      <div className="flex flex-wrap items-center gap-3 p-4 bg-primary/10 rounded-lg border border-primary/20">
          <span className="text-sm font-medium text-foreground">
            {selectedItems?.length}개 선택됨
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
            variant="outline"
            size="sm"
            iconName="CheckCircle"
            onClick={() => updateStatusForSelected('approved')}>

              일괄 승인
            </Button>
            <Button
            variant="destructive"
            size="sm"
            iconName="XCircle"
            onClick={() => updateStatusForSelected('rejected')}>

              일괄 거부
            </Button>
          </div>
        </div>
      }
      <div className="hidden lg:block bg-card rounded-lg shadow-gaming-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedItems?.length === filteredContent?.length && filteredContent?.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-border bg-background text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2" />

                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">콘텐츠</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">유형</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">신고자</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">사유</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">상태</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">신고 수</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredContent?.map((item) =>
              <tr key={item?.id} className="hover:bg-muted/30 transition-gaming">
                  <td className="px-6 py-4">
                    <input
                    type="checkbox"
                    checked={selectedItems?.includes(item?.id)}
                    onChange={() => handleSelectItem(item?.id)}
                    className="w-4 h-4 rounded border-border bg-background text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2" />

                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <Image
                      src={item?.image}
                      alt={item?.imageAlt}
                      className="w-16 h-16 rounded-md object-cover" />

                      <div>
                        <div className="text-sm font-medium text-foreground">{item?.title}</div>
                        <div className="text-xs text-muted-foreground caption">{item?.reportDate}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-foreground capitalize">
                      {item?.type === 'proof' ? '증명' : '챌린지'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Image
                      src={item?.userAvatar}
                      alt={item?.userAvatarAlt}
                      className="w-8 h-8 rounded-md object-cover" />

                      <span className="text-sm text-foreground">{item?.userName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-muted-foreground caption">{item?.reason}</span>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(item?.status)}</td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-foreground data-text">{item?.reportCount}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                      onClick={() => handleViewDetail(item)}
                      className="p-2 rounded-md hover:bg-muted transition-gaming"
                      aria-label="상세 보기">

                        <Icon name="Eye" size={16} className="text-muted-foreground" />
                      </button>
                      <button
                      onClick={() => handleApprove(item?.id)}
                      className="p-2 rounded-md hover:bg-success/10 transition-gaming"
                      aria-label="승인">

                        <Icon name="CheckCircle" size={16} className="text-success" />
                      </button>
                      <button
                      onClick={() => handleReject(item?.id)}
                      className="p-2 rounded-md hover:bg-destructive/10 transition-gaming"
                      aria-label="거부">

                        <Icon name="XCircle" size={16} className="text-destructive" />
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="lg:hidden space-y-3">
        {filteredContent?.map((item) =>
        <div key={item?.id} className="bg-card rounded-lg shadow-gaming-sm p-4 space-y-4">
            <div className="flex items-start space-x-3">
              <input
              type="checkbox"
              checked={selectedItems?.includes(item?.id)}
              onChange={() => handleSelectItem(item?.id)}
              className="w-4 h-4 rounded border-border bg-background text-primary focus:ring-2 focus:ring-primary flex-shrink-0 mt-1" />

              <Image
              src={item?.image}
              alt={item?.imageAlt}
              className="w-20 h-20 rounded-md object-cover flex-shrink-0" />

              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground mb-1">{item?.title}</div>
                <div className="text-xs text-muted-foreground caption mb-2">
                  {item?.type === 'proof' ? '증명' : '챌린지'}
                </div>
                {getStatusBadge(item?.status)}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Image
              src={item?.userAvatar}
              alt={item?.userAvatarAlt}
              className="w-8 h-8 rounded-md object-cover" />

              <div className="flex-1">
                <div className="text-sm text-foreground">{item?.userName}</div>
                <div className="text-xs text-muted-foreground caption">{item?.reportDate}</div>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Flag" size={14} className="text-destructive" />
                <span className="text-sm font-medium text-foreground data-text">{item?.reportCount}</span>
              </div>
            </div>

            <div className="p-3 bg-muted/50 rounded-md">
              <div className="text-xs text-muted-foreground caption mb-1">신고 사유</div>
              <div className="text-sm text-foreground">{item?.reason}</div>
            </div>

            <div className="flex items-center justify-end space-x-2 pt-3 border-t border-border">
              <Button
              variant="outline"
              size="sm"
              iconName="Eye"
              onClick={() => handleViewDetail(item)}>

                상세
              </Button>
              <Button
              variant="outline"
              size="sm"
              iconName="CheckCircle"
              onClick={() => handleApprove(item?.id)}>

                승인
              </Button>
              <Button
              variant="destructive"
              size="sm"
              iconName="XCircle"
              onClick={() => handleReject(item?.id)}>

                거부
              </Button>
            </div>
          </div>
        )}
      </div>
      {showDetailModal && selectedItem &&
      <div className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-gaming-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-heading font-semibold text-foreground">콘텐츠 상세</h3>
              <button
              onClick={() => setShowDetailModal(false)}
              className="p-2 rounded-md hover:bg-muted transition-gaming"
              aria-label="닫기">

                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="aspect-video w-full overflow-hidden rounded-lg">
                <Image
                src={selectedItem?.image}
                alt={selectedItem?.imageAlt}
                className="w-full h-full object-cover" />

              </div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm text-muted-foreground caption mb-1">제목</div>
                  <div className="text-base font-medium text-foreground">{selectedItem?.title}</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground caption mb-1">유형</div>
                    <div className="text-base text-foreground">
                      {selectedItem?.type === 'proof' ? '증명' : '챌린지'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground caption mb-1">상태</div>
                    {getStatusBadge(selectedItem?.status)}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground caption mb-1">작성자</div>
                  <div className="flex items-center space-x-2">
                    <Image
                    src={selectedItem?.userAvatar}
                    alt={selectedItem?.userAvatarAlt}
                    className="w-10 h-10 rounded-md object-cover" />

                    <span className="text-base text-foreground">{selectedItem?.userName}</span>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground caption mb-1">신고 사유</div>
                  <div className="text-base text-foreground">{selectedItem?.reason}</div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground caption mb-1">설명</div>
                  <div className="text-base text-foreground">{selectedItem?.description}</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground caption mb-1">신고 수</div>
                    <div className="text-base font-medium text-foreground data-text">
                      {selectedItem?.reportCount}건
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground caption mb-1">신고 날짜</div>
                    <div className="text-base text-foreground caption">{selectedItem?.reportDate}</div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  닫기
                </Button>
                <Button
                variant="outline"
                iconName="CheckCircle"
                onClick={() => {
                  handleApprove(selectedItem?.id);
                  setShowDetailModal(false);
                }}>

                  승인
                </Button>
                <Button
                variant="destructive"
                iconName="XCircle"
                onClick={() => {
                  handleReject(selectedItem?.id);
                  setShowDetailModal(false);
                }}>

                  거부
                </Button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>);

};

export default ContentModerationPanel;